import { gql, Resolver } from '@apollo/client';
import axios, { AxiosResponse } from 'axios';
import Papa from 'papaparse';
import HttpStatus from 'http-status-codes';

interface GetDetectionDataArgs {
    token: string;
    trqkDirectoryUrl: string;
    userId: string;
}

// const fakeHeaderRow =
//     'song_id,detection_time,detection_start,detection_stop,market,song_title,station_id,station_name\n';

// const dummyResponse = {
//     data: {
//         getTrqkDetectionsForUser: {
//             data:
//                 'song_id,detection_time,detection_start,detection_stop,market,song_title,station_id,station_name\n1597705108SE0039279820181U_200817230331_IllBeThereforYouLongVersion,1597807593477,2020-08-31T03:01:50Z,2020-08-31T03:02:03Z,National,Ill Be There for You Long Version,1916,Nickelodeon\n1597705108SE0039279820181U_200817230331_IllBeThereforYouLongVersion,1597807593477,2020-08-31T03:30:45Z,2020-08-31T03:31:27Z,National,Ill Be There for You Long Version,1916,Nickelodeon\n1597705108SE0039279820181U_200817230331_IllBeThereforYouLongVersion,1597807593477,2020-08-31T04:00:01Z,2020-08-31T04:00:45Z,National,Ill Be There for You Long Version,1916,Nickelodeon\n1597705108SE0039279820181U_200817230331_IllBeThereforYouLongVersion,1597807593477,2020-08-31T04:30:59Z,2020-08-31T04:31:42Z,National,Ill Be There for You Long Version,1916,Nickelodeon\n1597705108SE0039279820181U_200817230331_IllBeThereforYouLongVersion,1597807593477,2020-08-31T03:01:50Z,2020-08-31T03:02:03Z,National,Ill Be There for You Long Version,1916,Nickelodeon\n1597705108SE0039279820181U_200817230331_IllBeThereforYouLongVersion,1597807593477,2020-08-31T03:30:45Z,2020-08-31T03:31:27Z,National,Ill Be There for You Long Version,1916,Nickelodeon\n1597705108SE0039279820181U_200817230331_IllBeThereforYouLongVersion,1597807593477,2020-08-31T04:00:01Z,2020-08-31T04:00:45Z,National,Ill Be There for You Long Version,1916,Nickelodeon\n1597705108SE0039279820181U_200817230331_IllBeThereforYouLongVersion,1597807593477,2020-08-31T04:30:59Z,2020-08-31T04:31:42Z,National,Ill Be There for You Long Version,1916,Nickelodeon\n1597705108SE0039279820181U_200817230331_IllBeThereforYouLongVersion,1597807593477,2020-08-31T03:01:50Z,2020-08-31T03:02:03Z,National,Ill Be There for You Long Version,1916,Nickelodeon\n1597705108SE0039279820181U_200817230331_IllBeThereforYouLongVersion,1597807593477,2020-08-31T03:30:45Z,2020-08-31T03:31:27Z,National,Ill Be There for You Long Version,1916,Nickelodeon\n1597705108SE0039279820181U_200817230331_IllBeThereforYouLongVersion,1597807593477,2020-08-31T04:00:01Z,2020-08-31T04:00:45Z,National,Ill Be There for You Long Version,1916,Nickelodeon\n1597705108SE0039279820181U_200817230331_IllBeThereforYouLongVersion,1597807593477,2020-08-31T04:30:59Z,2020-08-31T04:31:42Z,National,Ill Be There for You Long Version,1916,Nickelodeon\n',
//         },
//     },
// };

// const mockAxiosPost = (): Promise<AxiosResponse<
//     GetTrqkDetectionsForUserResponse
// >> =>
//     new Promise((resolve): void => {
//         setTimeout((): void => {
//             resolve({
//                 data: dummyResponse,
//                 status: 200,
//                 statusText: 'success',
//                 config: {},
//                 headers: {},
//             });
//         }, 10);
//     });

/**
 * Fetches an array of detection data.
 * @param obj
 * @param {GetDetectionDataArgs} args
 * @returns {GetDetectionDataResult}
 */
export const getDetectionData: Resolver = async (
    obj: Record<string, unknown>,
    args: GetDetectionDataArgs,
): Promise<FetchDetectionDataResult> => {
    const { token, trqkDirectoryUrl, userId } = args;

    /**
     * Return a Promise that fetches the raw CSV data of the detections for a given userId, then
     * parses the data into an array of objects (one object per row in the CSV).
     * @param {string} statementId a statement ID
     */
    const fetchDetectionData = (): Promise<FetchDetectionDataResult> =>
        new Promise(resolve => {
            // mockAxiosPost()
            axios({
                method: 'post',
                url: trqkDirectoryUrl,
                headers: {
                    Authorization: token,
                },
                // throw an error for non-200 status
                validateStatus: (status: number): boolean =>
                    status === HttpStatus.OK,
                data: {
                    // We may implement a `since` timestamp in the query variables to filter the
                    // data on the backend before it is sent. For now, we will filter the data
                    // in the frontend.
                    query: `
                            query getTrqkDetectionsForUser($user_id: ID!) {
                                getTrqkDetectionsForUser(user_id: $user_id) {
                                    data
                                }
                            }
                        `,
                    variables: {
                        user_id: userId,
                    },
                },
            })
                .then(
                    (
                        response: AxiosResponse<GetTrqkDetectionsForUserResponse>,
                    ) => {
                        if (response?.data?.errors) {
                            throw new Error(
                                `Error(s) in GraphQL query: \n${JSON.stringify(
                                    response.data.errors,
                                    null,
                                    4,
                                )}`,
                            );
                        }

                        const csvString =
                            response?.data?.data?.getTrqkDetectionsForUser
                                ?.data ?? null;

                        if (csvString === null) {
                            throw new Error(
                                'Received empty data from getTrqkDetectionsForUser',
                            );
                        }

                        // Parse CSV string into a list of objects
                        const result = Papa.parse<RawDetection>(csvString, {
                            newline: '/n',
                            header: true,
                        });

                        const data = result?.data ?? ([] as RawDetection[]);

                        const nonEmptyData = data.filter(
                            (rawDetection: RawDetection): boolean =>
                                (rawDetection?.song_id?.length ?? 0) > 0,
                        );

                        resolve({
                            data: nonEmptyData,
                        });
                    },
                )
                .catch((error: Error) => {
                    // Resolve with an object that includes the statementId and error so that we
                    // can display some feedback to the user
                    resolve({
                        error,
                    });
                });
        });

    return fetchDetectionData();
};

export const GET_DETECTION_DATA = gql`
    query GetDetectionData(
        $token: String!
        $trqkDirectoryUrl: String!
        $userId: String!
    ) {
        getDetectionData(
            token: $token
            trqkDirectoryUrl: $trqkDirectoryUrl
            userId: $userId
        ) @client
    }
`;
